/**
 * Booking a statement row that has no receipt: bank charges or SVS to a category, a private
 * withdrawal or contribution, the Finanzamt, or a transfer to another own account. What it was
 * goes into the note, which is the Eigenbeleg. No VAT: input tax needs an invoice (§ 12 UStG).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useAccounts,
  useBookWithoutReceipt,
  useSaveBookingRule,
  useTransactions,
} from "@/hooks/use-banking";
import { useCategories } from "@/hooks/use-documents";
import type { BookingType, Transaction } from "@/lib/api/banking";
import { transferMirror } from "@/lib/banking/money";
import { counterpartyKey } from "@/lib/banking/rules";
import { useWorkspace } from "@/lib/session/workspace-context";

const TYPES: BookingType[] = ["category", "private", "tax", "transfer"];

export function BookingForm({
  transaction,
  onDone,
  onBack,
}: {
  transaction: Transaction;
  onDone: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const { workspace } = useWorkspace();
  const { data: categories } = useCategories(workspace?.id);
  const { data: accounts } = useAccounts(workspace?.id);
  const { data: transactions } = useTransactions(workspace?.id);
  const book = useBookWithoutReceipt();
  const saveRule = useSaveBookingRule();
  const [remember, setRemember] = useState(false);
  const who = transaction.counterparty || transaction.description || "";
  const [type, setType] = useState<BookingType>(
    (transaction.bookingType as BookingType) || "category",
  );
  const [category, setCategory] = useState(transaction.category ?? "");
  const [transferAccount, setTransferAccount] = useState(transaction.transferAccount ?? "");
  const [note, setNote] = useState(transaction.bookingNote ?? "");

  const options = categories ?? [];
  const others = (accounts ?? []).filter((account) => account.id !== transaction.account);
  const ready =
    (type !== "category" || Boolean(category)) && (type !== "transfer" || Boolean(transferAccount));

  async function save() {
    const mirror =
      type === "transfer" ? transferMirror(transaction, transferAccount, transactions ?? []) : null;
    const input = { bookingType: type, category, transferAccount, bookingNote: note };
    await book.mutateAsync({ transaction, input, mirror });
    if (remember) await saveRule.mutateAsync({ transaction, input });
    toast.success(mirror ? t("booking.savedTransfer") : t("booking.saved"));
    onDone();
  }

  return (
    <div className="grid gap-4" data-testid="booking-form">
      <p className="text-sm text-muted-foreground">{t("booking.hint")}</p>
      <div className="space-y-1.5">
        <Label htmlFor="booking-type">{t("booking.type")}</Label>
        <Select value={type} onValueChange={(value) => setType(value as BookingType)}>
          <SelectTrigger id="booking-type" data-testid="booking-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TYPES.map((option) => (
              <SelectItem key={option} value={option}>
                {t(`booking.types.${option}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{t(`booking.typeHints.${type}`)}</p>
      </div>

      {type === "category" && (
        <div className="space-y-1.5">
          <Label htmlFor="booking-category">{t("booking.category")}</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger id="booking-category" data-testid="booking-category">
              <SelectValue placeholder={t("booking.chooseCategory")} />
            </SelectTrigger>
            <SelectContent>
              {options.map((entry) => (
                <SelectItem key={entry.id} value={entry.id}>
                  {entry.accountNumber ? `${entry.accountNumber} · ${entry.name}` : entry.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {type === "transfer" && (
        <div className="space-y-1.5">
          <Label htmlFor="booking-transfer">{t("booking.transferAccount")}</Label>
          <Select value={transferAccount} onValueChange={setTransferAccount}>
            <SelectTrigger id="booking-transfer" data-testid="booking-transfer">
              <SelectValue placeholder={t("booking.chooseAccount")} />
            </SelectTrigger>
            <SelectContent>
              {others.map((account) => (
                <SelectItem key={account.id} value={account.id}>
                  {account.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="booking-note">{t("booking.note")}</Label>
        <Input
          id="booking-note"
          value={note}
          maxLength={300}
          placeholder={t("booking.notePlaceholder")}
          onChange={(event) => setNote(event.target.value)}
          data-testid="booking-note"
        />
      </div>

      {counterpartyKey(transaction) && (
        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            checked={remember}
            onCheckedChange={(value) => setRemember(value === true)}
            className="mt-0.5"
            data-testid="booking-remember"
          />
          <span>
            {t("booking.remember", { counterparty: who })}
            <span className="block text-xs text-muted-foreground">{t("booking.rememberHint")}</span>
          </span>
        </label>
      )}

      <div className="flex flex-wrap justify-between gap-2">
        <Button variant="ghost" onClick={onBack}>
          {t("booking.back")}
        </Button>
        <Button
          onClick={() => void save()}
          disabled={!ready || book.isPending || saveRule.isPending}
          data-testid="save-booking"
        >
          {t("booking.save")}
        </Button>
      </div>
    </div>
  );
}
